import { AppChannel } from '../types';

/**
 * ─── Détection du canal d'ouverture ─────────────────────────────────────────
 *
 * Règle métier : l'application est distribuée sur trois canaux, identifiés par
 * le PRÉFIXE de l'URL (segment de base) et conservés partout via le `basename`
 * du routeur :
 *
 *   /maxit/…  → Super App Maxit (Orange)   → Orange Money uniquement
 *   /mpesa/…  → Super App M-Pesa (Vodacom) → carte Visa M-Pesa + M-Pesa
 *   racine    → web / PWA / stores (monde + RDC) → FlexPay (carte + mobile money)
 *
 * Le préfixe étant la base de TOUTES les routes, il « suit » l'utilisateur quel
 * que soit l'endroit où il se trouve dans l'app (exigence d'Orange/Vodacom).
 *
 * Pour tester sans partenaire, `?channel=maxit|mpesa` force le canal et le
 * mémorise pour la session (utile quand on n'a pas de préfixe de chemin).
 */

const CHANNEL_QUERY_PARAM = 'channel';
const CHANNEL_SESSION_KEY = 'payment-channel';

/** Segment d'URL de base → canal. Sert aussi au `basename` du routeur. */
const PREFIX_TO_CHANNEL: Record<string, AppChannel> = {
  maxit: AppChannel.MAXIT,
  mpesa: AppChannel.MPESA_SUPER_APP,
};

declare global {
  interface Window {
    /** Bridge natif injecté par une Super App (nom à confirmer avec la doc). */
    MpesaBridge?: unknown;
    OrangeBridge?: unknown;
  }
}

/** Premier segment du chemin (hors basename déjà retiré par le routeur). */
function firstPathSegment(pathname: string): string {
  return pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase() ?? '';
}

/**
 * Préfixe de chemin à passer en `basename` au routeur. Détecté une seule fois
 * depuis l'URL brute (avant que le routeur ne retire le basename). Retourne
 * `''` (racine) pour le web, `/maxit` ou `/mpesa` pour une Super App.
 */
export function getRouterBasename(): string {
  if (typeof window === 'undefined') return '';
  const seg = firstPathSegment(window.location.pathname);
  return PREFIX_TO_CHANNEL[seg] ? `/${seg}` : '';
}

/**
 * Canal courant. Priorités :
 *   1. préfixe d'URL (source de vérité — la base « suit » l'utilisateur)
 *   2. paramètre `?channel=` explicite (tests) — mémorisé pour la session
 *   3. choix mémorisé pour la session
 *   4. bridge natif injecté par la Super App
 */
export function detectChannel(): AppChannel {
  if (typeof window === 'undefined') return AppChannel.WEB;

  // 1. Préfixe d'URL : c'est la base de toutes les routes du canal.
  const fromPrefix = PREFIX_TO_CHANNEL[firstPathSegment(window.location.pathname)];
  if (fromPrefix) {
    sessionStorage.setItem(CHANNEL_SESSION_KEY, fromPrefix);
    return fromPrefix;
  }

  // 2. Paramètre d'URL explicite (tests manuels sans préfixe de chemin).
  const fromQuery = new URLSearchParams(window.location.search).get(
    CHANNEL_QUERY_PARAM,
  );
  const fromQueryChannel = fromQuery ? PREFIX_TO_CHANNEL[fromQuery] : undefined;
  if (fromQueryChannel) {
    sessionStorage.setItem(CHANNEL_SESSION_KEY, fromQueryChannel);
    return fromQueryChannel;
  }

  // 3. Choix mémorisé pour la session.
  const stored = sessionStorage.getItem(CHANNEL_SESSION_KEY);
  if (stored === AppChannel.MAXIT || stored === AppChannel.MPESA_SUPER_APP) {
    return stored;
  }

  // 4. Bridges natifs injectés par les Super Apps (noms à confirmer).
  if (window.OrangeBridge !== undefined) return AppChannel.MAXIT;
  if (window.MpesaBridge !== undefined) return AppChannel.MPESA_SUPER_APP;

  return AppChannel.WEB;
}
